import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Mic, FileText, BarChart3, Download, Clock, User, Hash, History, Pencil, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import api from "../lib/api";
import { ActivityLog } from "../components/ActivityLog";
import { useLanguage } from "../context/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { languages as allLanguages } from "../lib/languages";

const statusConfig = {
  created: { step: 1, route: "record", color: "bg-slate-100 text-slate-700 border-slate-200" },
  audio_uploaded: { step: 2, route: "record", color: "bg-blue-50 text-blue-700 border-blue-200" },
  transcribed: { step: 3, route: "transcript", color: "bg-amber-50 text-amber-700 border-amber-200" },
  analyzed: { step: 4, route: "results", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const getScoreColor = (score) => {
  if (!score) return "bg-slate-100 text-slate-500";
  if (score <= 2) return "bg-red-50 text-red-700";
  if (score === 3) return "bg-amber-50 text-amber-700";
  return "bg-emerald-50 text-emerald-700";
};

export default function CaseDetail() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { currentLanguage, changeLanguage, t } = useLanguage();

  const [caseData, setCaseData] = useState(null);

  // Sync UI language with case target language
  useEffect(() => {
    if (caseData?.target_language && currentLanguage && caseData.target_language !== currentLanguage.code) {
      let targetLang = allLanguages.find(l => l.code === caseData.target_language);
      if (!targetLang) {
        for (const l of allLanguages) {
          if (l.dialects) {
            const d = l.dialects.find(dial => dial.code === caseData.target_language);
            if (d) {
              targetLang = d;
              break;
            }
          }
        }
      }
      if (targetLang) {
        changeLanguage(targetLang);
      }
    }
  }, [caseData?.target_language, currentLanguage?.code, changeLanguage]);
  const [scores, setScores] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [caseId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const caseRes = await api.getCase(caseId);
      setCaseData(caseRes.data);

      if (caseRes.data.status === "analyzed") {
        const scoresRes = await api.getScores(caseId);
        setScores(scoresRes.data);
      }

      try {
        const historyRes = await api.getClientHistory(caseRes.data.client_id);
        setHistory(historyRes.data.assessments || []);
      } catch (e) {
        // History might not exist
      }
    } catch (error) {
      console.error("Failed to load case:", error);
      toast.error("Failed to load case");
    } finally {
      setLoading(false);
    }
  };

  const [editOpen, setEditOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editData, setEditData] = useState({ client_name: "", client_id: "", postal_code: "" });

  const handleStartEdit = () => {
    setEditData({
      client_name: caseData.client_name || "",
      client_id: caseData.client_id || "",
      postal_code: caseData.postal_code || "",
    });
    setEditOpen(true);
  };

  const handleUpdateCase = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      await api.updateCaseDetails(caseId, editData);
      toast.success(t('success_case_updated') || "Case details updated");
      setEditOpen(false);
      loadData(); // Reload to show new info
    } catch (error) {
      console.error("Failed to update case:", error);
      toast.error(t('error_update_case') || "Failed to update case details");
    } finally {
      setUpdating(false);
    }
  };

  const getNextAction = () => {
    if (!caseData) return null;

    switch (caseData.status) {
      case "created":
        return {
          label: t('record_conversation'),
          icon: Mic,
          path: `/case/${caseId}/record`,
          description: t('record_desc'),
        };
      case "audio_uploaded":
        return {
          label: t('view_transcript'),
          icon: FileText,
          path: `/case/${caseId}/transcript`,
          description: t('view_transcript_desc'),
        };
      case "transcribed":
        return {
          label: t('run_analysis'),
          icon: BarChart3,
          path: `/case/${caseId}/transcript`,
          description: t('analyze_desc'),
        };
      case "analyzed":
        return {
          label: t('view_results'),
          icon: BarChart3,
          path: `/case/${caseId}/results`,
          description: t('view_results_desc'),
        };
      default:
        return null;
    }
  };

  const handleExportPdf = () => {
    const token = localStorage.getItem("auth_token");
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/export/pdf`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">{t('loading')}</p>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">{t('case_not_found')}</p>
      </div>
    );
  }

  const nextAction = getNextAction();
  const effectiveStatus = caseData.status === "submitted" ? "analyzed" : (caseData.status || "created");
  const status = statusConfig[effectiveStatus] || statusConfig.created;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" data-testid="back-to-dashboard">
                <Button variant="ghost" size="sm" className="rounded-sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('dashboard_breadcrumb')}
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div>
                <h1 className="text-xl font-bold text-slate-900 font-['Manrope']" data-testid="case-detail-title">
                  {caseData.client_name}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <Badge variant="outline" className={`status-pill ${status.color}`}>
                    {t(`status_${effectiveStatus}`)}
                  </Badge>
                  <span className="text-sm text-slate-500 font-mono">{caseData.client_id}</span>
                  <span className="text-sm text-slate-500">{t('assessment_number')} {caseData.assessment_number || 1}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <Link to={`/new-case?client_name=${encodeURIComponent(caseData.client_name)}&client_id=${encodeURIComponent(caseData.client_id)}&postal_code=${encodeURIComponent(caseData.postal_code || "")}`}>
                <Button variant="outline" className="rounded-sm">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('start_new_assessment')}
                </Button>
              </Link>
              {caseData.status === "analyzed" && (
                <Button
                  onClick={handleExportPdf}
                  className="bg-slate-700 hover:bg-slate-800 rounded-sm"
                  data-testid="export-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('export_pdf')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Case Info */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base font-['Manrope']">{t('case_information')}</CardTitle>
              <Button variant="ghost" size="sm" onClick={handleStartEdit} className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700">
                <Pencil className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">{t('client_name_label')}:</span>
                <span className="text-sm font-medium text-slate-900">{caseData.client_name}</span>
              </div>
              <div className="flex items-center gap-3">
                <Hash className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">{t('client_id_label')}:</span>
                <span className="text-sm font-medium text-slate-900 font-mono">{caseData.client_id}</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">{t('assessment_date')}:</span>
                <span className="text-sm font-medium text-slate-900">{caseData.assessment_date}</span>
              </div>
            </CardContent>
          </Card>

          {/* Next Action */}
          {nextAction && (
            <Card className="border-slate-200 rounded-sm bg-slate-700 text-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold font-['Manrope'] mb-2">{t('next_step')}</h3>
                <p className="text-slate-300 text-sm mb-4">{nextAction.description}</p>
                <Button
                  onClick={() => navigate(nextAction.path)}
                  className="bg-white text-slate-900 hover:bg-slate-100 rounded-sm"
                  data-testid="next-action-btn"
                >
                  <nextAction.icon className="w-4 h-4 mr-2" />
                  {nextAction.label}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Workflow Progress */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Manrope']">{t('assessment_workflow')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between relative px-2">
              {/* Progress Bar Background */}
              <div className="absolute top-5 left-2 right-2 h-0.5 bg-slate-200 -z-10" />

              {/* Active Progress Bar */}
              <div
                className="absolute top-5 left-2 h-0.5 bg-slate-700 -z-10 transition-all duration-500"
                style={{ width: `${((status.step - 1) / (Object.keys(statusConfig).length - 1)) * 100}%` }}
              />

              {Object.entries(statusConfig).map(([key, config]) => (
                <Link
                  key={key}
                  to={`/case/${caseId}/${config.route}`}
                  className={`flex flex-col items-center group relative z-0 bg-slate-50 px-2 ${caseData.status === "created" && config.step > 1 ? "pointer-events-none opacity-60" : "cursor-pointer"}`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${status.step >= config.step
                      ? "bg-slate-700 text-white group-hover:bg-slate-800 shadow-sm"
                      : "bg-slate-200 text-slate-500"
                      }`}
                  >
                    {config.step}
                  </div>
                  <span className={`text-xs mt-2 font-medium transition-colors ${status.step >= config.step ? "text-slate-900" : "text-slate-400"
                    }`}>
                    {t(`status_${key}`)}
                  </span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Scores Summary (if analyzed) */}
        {caseData.status === "analyzed" && scores.length > 0 && (
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-['Manrope']">{t('domain_scores_summary')}</CardTitle>
                <Link to={`/case/${caseId}/results`}>
                  <Button variant="link" className="text-sm p-0 h-auto">
                    {t('view_full_results')}
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-10 gap-2">
                {scores.slice(0, 19).map((score) => {
                  const finalScore = score.human_score || score.ai_score;
                  return (
                    <div
                      key={score.domain_key}
                      className="flex flex-col items-center p-2 rounded-sm bg-slate-50"
                      title={t(`domain_${score.domain_key}`) !== `domain_${score.domain_key}` ? t(`domain_${score.domain_key}`) : score.domain_label}
                    >
                      <span className={`w-8 h-8 flex items-center justify-center rounded-sm text-sm font-semibold ${getScoreColor(finalScore)}`}>
                        {finalScore || "-"}
                      </span>
                      <span className="text-xs text-slate-500 mt-1 truncate max-w-full">
                        {t(`domain_${score.domain_key}`) !== `domain_${score.domain_key}`
                          ? (t(`domain_${score.domain_key}`).split(" ")[0])
                          : score.domain_label?.split(" ")[0]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Longitudinal History */}
        {history.length > 0 && (
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-['Manrope'] flex items-center gap-2">
                <History className="w-4 h-4" />
                {t('assessment_history')}
              </CardTitle>
              <CardDescription>
                {t('track_progress')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {history.map((assessment) => (
                  <div
                    key={assessment.case_id}
                    className={`flex items-center justify-between p-3 rounded-sm border ${assessment.case_id === caseId
                      ? "border-slate-400 bg-slate-50"
                      : "border-slate-200"
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-medium text-slate-900">{t('assessment_number')} {assessment.assessment_number || 1}</div>
                        <div className="text-xs text-slate-500">{assessment.assessment_date}</div>
                      </div>
                      {assessment.case_id === caseId && (
                        <Badge variant="outline" className="bg-slate-100">{t('current')}</Badge>
                      )}
                    </div>
                    {assessment.case_id !== caseId && (
                      <Link to={`/case/${assessment.case_id}`}>
                        <Button variant="ghost" size="sm" className="rounded-sm">
                          {t('view')}
                        </Button>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card className="border-slate-200 rounded-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-['Manrope']">{t('quick_actions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to={`/case/${caseId}/record`}>
                <Button variant="outline" className="rounded-sm" data-testid="go-to-recording">
                  <Mic className="w-4 h-4 mr-2" />
                  {t('recordings')}
                </Button>
              </Link>
              <Link to={`/case/${caseId}/transcript`}>
                <Button variant="outline" className="rounded-sm" data-testid="go-to-transcript">
                  <FileText className="w-4 h-4 mr-2" />
                  {t('transcript') || 'Transcript'}
                </Button>
              </Link>
              <Link to={`/case/${caseId}/results`}>
                <Button variant="outline" className="rounded-sm" data-testid="go-to-results">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {t('results') || 'Results'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <ActivityLog caseData={caseData} />
      </main>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('edit_case_details')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateCase} className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">{t('client_name_label')} *</Label>
              <Input
                id="edit-name"
                value={editData.client_name}
                onChange={(e) => setEditData({ ...editData, client_name: e.target.value })}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-id">{t('client_id_label')} *</Label>
              <Input
                id="edit-id"
                value={editData.client_id}
                onChange={(e) => setEditData({ ...editData, client_id: e.target.value })}
                className="col-span-3 font-mono"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-postal">{t('postal_code_label')} *</Label>
              <Input
                id="edit-postal"
                value={editData.postal_code}
                onChange={(e) => setEditData({ ...editData, postal_code: e.target.value })}
                className="col-span-3"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" disabled={updating}>
                {updating ? t('saving') : t('save_changes_btn')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
